import { HTMLAttributes, forwardRef } from 'react';

type BadgeVariant = 'default' | 'primary' | 'success' | 'warning' | 'error' | 'outline';
type BadgeSize = 'sm' | 'md' | 'lg';

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
  size?: BadgeSize;
  dot?: boolean;
}

const badgeVariants: Record<BadgeVariant, string> = {
  default: 'bg-slate-100 text-slate-700',
  primary: 'bg-blue-100 text-blue-700',
  success: 'bg-green-100 text-green-700',
  warning: 'bg-amber-100 text-amber-700',
  error: 'bg-red-100 text-red-700',
  outline: 'bg-transparent border border-slate-300 text-slate-700',
};

const badgeSizes: Record<BadgeSize, string> = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-sm',
  lg: 'px-3 py-1 text-sm',
};

export const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'default', size = 'md', dot = false, className = '', children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center gap-1.5
          font-medium rounded-full
          ${badgeVariants[variant]}
          ${badgeSizes[size]}
          ${className}
        `}
        {...props}
      >
        {dot && <span className="w-1.5 h-1.5 rounded-full bg-current" />}
        {children}
      </span>
    );
  }
);

Badge.displayName = 'Badge';

interface DifficultyBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  level: 1 | 2 | 3 | 4 | 5;
}

const difficultyStyles: Record<number, string> = {
  1: 'bg-green-100 text-green-700 border-green-200',
  2: 'bg-emerald-100 text-emerald-700 border-emerald-200',
  3: 'bg-yellow-100 text-yellow-700 border-yellow-200',
  4: 'bg-orange-100 text-orange-700 border-orange-200',
  5: 'bg-red-100 text-red-700 border-red-200',
};

const difficultyLabels: Record<number, string> = {
  1: '基础',
  2: '简单',
  3: '中等',
  4: '困难',
  5: '挑战',
};

export const DifficultyBadge = forwardRef<HTMLSpanElement, DifficultyBadgeProps>(
  ({ level, className = '', ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center gap-1
          px-2 py-0.5 text-xs font-medium
          rounded-full border
          ${difficultyStyles[level]}
          ${className}
        `}
        {...props}
      >
        <span className="w-1 h-1 rounded-full bg-current" />
        {difficultyLabels[level]}
      </span>
    );
  }
);

DifficultyBadge.displayName = 'DifficultyBadge';

interface GradeBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  grade: 7 | 8 | 9 | 10 | 11 | 12;
}

const gradeStyles: Record<number, string> = {
  7: 'bg-indigo-100 text-indigo-700',
  8: 'bg-purple-100 text-purple-700',
  9: 'bg-pink-100 text-pink-700',
  10: 'bg-rose-100 text-rose-700',
  11: 'bg-amber-100 text-amber-700',
  12: 'bg-orange-100 text-orange-700',
};

export const GradeBadge = forwardRef<HTMLSpanElement, GradeBadgeProps>(
  ({ grade, className = '', ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center
          px-2.5 py-0.5 text-sm font-medium
          rounded-full
          ${gradeStyles[grade]}
          ${className}
        `}
        {...props}
      >
        Y{grade}
      </span>
    );
  }
);

GradeBadge.displayName = 'GradeBadge';

interface MasteryBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  level: number;
}

const getMasteryStyle = (level: number): string => {
  if (level >= 0.8) return 'bg-green-100 text-green-700 border-green-300';
  if (level >= 0.6) return 'bg-blue-100 text-blue-700 border-blue-300';
  if (level >= 0.4) return 'bg-yellow-100 text-yellow-700 border-yellow-300';
  if (level >= 0.2) return 'bg-orange-100 text-orange-700 border-orange-300';
  return 'bg-slate-100 text-slate-700 border-slate-300';
};

const getMasteryLabel = (level: number): string => {
  if (level >= 0.8) return '精通';
  if (level >= 0.6) return '熟练';
  if (level >= 0.4) return '学习中';
  if (level >= 0.2) return '入门';
  return '未开始';
};

export const MasteryBadge = forwardRef<HTMLSpanElement, MasteryBadgeProps>(
  ({ level, className = '', ...props }, ref) => {
    const normalizedLevel = Math.min(1, Math.max(0, level));

    return (
      <span
        ref={ref}
        className={`
          inline-flex items-center gap-1.5
          px-2.5 py-1 text-sm font-medium
          rounded-full border
          ${getMasteryStyle(normalizedLevel)}
          ${className}
        `}
        {...props}
      >
        <span>{getMasteryLabel(normalizedLevel)}</span>
        <span className="opacity-70">{Math.round(normalizedLevel * 100)}%</span>
      </span>
    );
  }
);

MasteryBadge.displayName = 'MasteryBadge';
